/**
 * Card JoKjdQUR — Cleanup TTL del conversationId persistido en localStorage.
 *
 * Followup de la card de retención de datos anónimos (Card #6): el frontend
 * aplica la misma política de 90 días sobre el ``conversationId`` que persiste
 * la card 1wakgQ4p en ``localStorage["inbest-chat-conversation-id"]`` con
 * shape ``{id, createdAt}``.
 *
 * Política
 * --------
 * - **TTL = 90 días** (alineado con la purga server-side de ``prospect``).
 * - Si ``Date.now() - createdAt > TTL_MS`` → borramos la key. El próximo read
 *   de ``chatUIStore`` (factory) genera un id nuevo y lo persiste — el viejo
 *   nunca vuelve a viajar al backend.
 * - Si la key no existe → no-op.
 * - Si el JSON es corrupto o no tiene ``createdAt`` numérico → borramos
 *   (defensivo: una versión vieja sin timestamp NO se puede edad-chequear,
 *   tratarla como vencida es seguro y se regenera limpia).
 * - Si ``localStorage`` no está disponible (private mode / SecurityError) →
 *   no-op silencioso. No bloqueamos la UX.
 *
 * Wiring
 * ------
 * - ``main.tsx``: ``cleanupExpiredConversation()`` ANTES de importar ``App``
 *   (que termina importando ``chatUIStore``) para que el factory del store
 *   vea la key ya removida y arme un id fresco.
 * - ``hooks/useAuth.ts``: ``clearConversationOnLogout()`` antes del
 *   ``logout()`` de Auth0 — en logout limpiamos todo el conversationId,
 *   no sólo los expirados, para no arrastrar la sesión anónima de un user
 *   al próximo login (criterio implícito del workflow).
 *
 * Tests
 * -----
 * Ver ``__tests__/conversationCleanup.test.ts`` — cubre 0 keys, key fresca,
 * key vencida, JSON corrupto, ``createdAt`` ausente, y SecurityError.
 */

const STORAGE_KEY = 'inbest-chat-conversation-id';

/** TTL alineado con la política de retención server-side (Card #6 = 90 días). */
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Borra el ``conversationId`` persistido si tiene más de 90 días.
 *
 * @param now Timestamp de referencia (default ``Date.now()``). Inyectable
 *   para tests deterministas — no pisar en producción.
 * @returns ``1`` si se removió una entry vencida, ``0`` si no había nada
 *   que limpiar (key ausente, fresca, o storage no disponible).
 */
export function cleanupExpiredConversation(now: number = Date.now()): number {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 0;

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;

    let createdAt: number | undefined;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        parsed &&
        typeof parsed === 'object' &&
        typeof (parsed as { createdAt?: unknown }).createdAt === 'number'
      ) {
        createdAt = (parsed as { createdAt: number }).createdAt;
      }
    } catch {
      // JSON corrupto. Por defecto NO limpiamos JSON ilegible: el factory
      // del store ya lo degrada a un id nuevo y persiste el shape canónico
      // de inmediato (test "JSON corrupto degrada a UUID nuevo"). No
      // queremos ocultar evidencia de corrupción borrando acá.
      return 0;
    }

    // Sin ``createdAt`` numérico no podemos chequear edad. Lo tratamos como
    // legacy/vencido y limpiamos: el siguiente render genera un id fresco
    // en shape canónico ``{id, createdAt}``.
    if (createdAt === undefined) {
      window.localStorage.removeItem(STORAGE_KEY);
      return 1;
    }

    if (now - createdAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return 1;
    }

    return 0;
  } catch {
    // SecurityError / private mode → silencioso. La app sigue funcionando
    // con el id en memoria; el TTL se reintentará en el próximo arranque.
    return 0;
  }
}

/**
 * Borra el ``conversationId`` persistido sin chequear TTL.
 *
 * Llamado desde ``useAuth.logout`` antes de redirigir a Auth0 para que la
 * sesión anónima de un usuario NO se herede al próximo login del mismo
 * navegador. Es complementario al cleanup de TTL: ese aplica al arranque,
 * éste al cierre de sesión.
 *
 * @returns ``1`` si se removió una entry, ``0`` si no había nada o el
 *   storage no estaba disponible.
 */
export function clearConversationOnLogout(): number {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return 0;
    const existed = window.localStorage.getItem(STORAGE_KEY) !== null;
    if (!existed) return 0;
    window.localStorage.removeItem(STORAGE_KEY);
    return 1;
  } catch {
    return 0;
  }
}

/** Exportado sólo para tests — TTL en ms. */
export const __CONVERSATION_TTL_MS__ = TTL_MS;
/** Exportado sólo para tests — key de localStorage. */
export const __CONVERSATION_STORAGE_KEY__ = STORAGE_KEY;
